<template>
  <v-container fluid> <!-- Full-width layout container -->
    <v-row> <!-- A horizontal row -->

      <!-- 📍 Right Column -->
      <v-col cols="12" md="12">

        <!-- 👷 Contractor Management -->
        <v-card class="pa-4">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="text-h6 font-weight-bold">Contractors</h3>
              <v-btn  color="success"  @click="handleAddContractorClick"  prepend-icon="mdi-plus"> Add Contractor </v-btn>
          </div>

          <!-- Contractor Table -->
          <v-data-table
            :headers="contractorHeaders"
            :items="contractors"
            class="elevation-1 customer-setting-contractors-table"
            density="compact"
            @click:row="onRowClick"
          >
            <!-- Actions column (edit/delete) -->
            <template #item.actions="{ item }">
              <v-btn icon size="small" @click="editContractor(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon size="small" @click="deleteContractor(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>

        <!-- 🗄️ Archive & 📜 Activity Log -->
        <v-row class="mt-4">
          <v-col cols="12" md="6">
            <div class="font-weight-bold text-subtitle-1 mb-1">Archive</div>
            <div class="text-caption mb-2">View all archived files</div>
            <v-btn color="success" variant="flat" size="small" @click="viewArchive">
              VIEW ARCHIVE
            </v-btn>
          </v-col>

          <v-col cols="12" md="6">
            <div class="font-weight-bold text-subtitle-1 mb-1">Activity Log</div>
            <div class="text-caption mb-2">View all activity on WayWyser</div>
            <v-btn color="success" variant="flat" size="small" @click="viewActivityLog">
              VIEW ACTIVITY LOG
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>

  <!-- Add/Edit Contractor Modal -->
<v-dialog v-model="showContractorModal" persistent max-width="500px">
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <span class="text-h6 font-weight-bold">
        {{ isEditingContractor ? "Edit Contractor" : "Add Contractor" }}
      </span>
      <v-btn flat icon @click="showContractorModal = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-form ref="contractorForm" lazy-validation>
        <v-text-field
          v-model="editContractorData.contractorName"
          label="Contractor Name"
          required
        />
        <v-text-field
          v-model="editContractorData.contactName"
          label="Contact Name"
          required
        />
        <v-text-field
          v-model="editContractorData.location"
          label="Location"
        />
        <v-text-field
          v-model="editContractorData.lastWorkOrder"
          label="Last Work Order"
        />
        <v-text-field
          v-model="editContractorData.email"
          label="Email"
          required
        />
        <v-text-field
          v-model="editContractorData.phone"
          label="Phone"
          required
        />
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn variant="text" @click="showContractorModal = false">Cancel</v-btn>
      <v-btn color="green" @click="saveContractor">
        {{ isEditingContractor ? "Update" : "Add" }} Contractor
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

</template>

<script>
// Map Settings moved to Utilities > Map Settings
// Map Overlays moved to Utilities > Map Overlays
import { getContractors, createContractor, updateContractor, deleteContractor as deleteContractorApi } from '@/api/Contractors';
import { auditLog } from '@/api/audit';
import { useDialogStore } from "@/stores/dialog";

// Add this function for the button click
const handleAddContractorClick = async () => {
  // Log the button click
  await auditLog.contractorAddClicked('Customer Settings');

  // Your existing logic (open modal, navigate, etc.)
  showAddContractorDialog.value = true; // or whatever you currently do
};


export default {
  name: "SettingsPage",

  mounted() {
    this.loadContractors();
  },

  data() {
    return {
      contractors: [],
      contractorHeaders: [
        { title: "Name", key: "contractorName" },
        { title: "Contact", key: "contactName" },
        { title: "Location", key: "location" },
        { title: "Last Work Order", key: "lastWorkOrder" },
        { title: "Actions", key: "actions", sortable: false },
      ],
      showContractorModal: false,
      isEditingContractor: false,
      editContractorData: {
        contractorId: null,
        contractorName: "",
        contactName: "",
        location: "",
        email: "",
        phone: "",
        lastWorkOrder: ""
      },
    };
  },

  methods: {
    async handleAddContractorClick() {
      await auditLog.contractorAddClicked('Customer Settings');
      this.addContractor();
    },

    async deleteContractor(item) {
      const result = await this.dialog.confirm({
        title: "Delete Contractor",
        message: `Delete ${item.contractorName}?`,
        variant: "warn",
        buttons: "ok-cancel"
      });
      if (result !== "ok") return;
      try {
        await deleteContractorApi(item.contractorId);
        if (this.$notify && this.$notify.success) this.$notify.success("Contractor deleted");
        this.loadContractors();
      } catch (err) {
        if (this.$notify && this.$notify.error) this.$notify.error("Failed to delete contractor");
      }
    },

 addContractor() {
  this.editContractorData = {
    contractorId: 0, // <-- Use 0 for new
    contractorName: "",
    contactName: "",
    location: "",
    email: "",
    phone: "",
    lastWorkOrder: ""
  };
  this.isEditingContractor = false;
  this.showContractorModal = true;
},

onRowClick(event, payload) {
  const item = payload?.item ?? payload;
  if (item) this.editContractor(item);
},
editContractor(item) {
  this.editContractorData = {
    contractorId: item.contractorId || item.id || null,
    contractorName: item.contractorName || "",
    contactName: item.contactName || "",
    location: item.location || "",
    email: item.email || "",
    phone: item.phone || "",
    lastWorkOrder: item.lastWorkOrder || ""
  };
  this.isEditingContractor = true;
  this.showContractorModal = true;
},

// ...existing code...
 async saveContractor() {
      try {

        // Validate required fields
        if (!this.editContractorData.contractorName || !this.editContractorData.contactName) {
          this.$notify?.error("Contractor Name and Contact Name are required");
          return;
        }

        // Ensure all fields have values, replace null/undefined with empty strings
        const contractorPayload = {
          contractorId: this.editContractorData.contractorId || 0,
          contractorName: this.editContractorData.contractorName || "",
          contactName: this.editContractorData.contactName || "",
          location: this.editContractorData.location || "",
          email: this.editContractorData.email || "",
          phone: this.editContractorData.phone || "",
          lastWorkOrder: this.editContractorData.lastWorkOrder || ""
        };


        if (this.isEditingContractor && contractorPayload.contractorId > 0) {
          await updateContractor(contractorPayload);
          this.$notify?.success("Contractor updated");
        } else {
          // Remove contractorId for new contractors
          delete contractorPayload.contractorId;
          const newContractor = await createContractor(contractorPayload);

          // ADD THIS LINE - Log the contractor creation
          await auditLog.contractorCreated(newContractor.contractorId, newContractor.contractorName);

          this.$notify?.success("Contractor added");
        }
        this.showContractorModal = false;
        this.loadContractors();
      } catch (err) {
        this.$notify?.error(`Failed to save contractor: ${err.response?.data?.message || err.message}`);
      }
    },

    async loadContractors() {
      try {
        this.contractors = await getContractors();
      } catch (err) {
        this.$notify?.error("Failed to load contractors");
      }
    },


    viewArchive() {
    },

    viewActivityLog() {
      // Navigate to the AuditGrid/Audit Trail page
      this.$router.push({ name: 'AuditTrail' });
    }


  }
};
</script>

<style scoped>
.customer-setting-contractors-table :deep(.v-data-table__tr) {
  cursor: pointer;
}
.customer-setting-contractors-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
</style>
